import { type PointerEvent, type WheelEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Input from "../../../../components/Form/Input/input";
import { Form, FormGroup } from "../../../../components/Form/styles";
import { Box } from "../../../../components/Layout";
import SaveBar from "../../../../components/SaveBar";
import { SubHeader } from "../../../../components/SubHeader";
import { LocationSection } from "../../../../model/site";
import { useSection } from "../../hooks/Section";
import { Container, MapPreview } from "./styles";

type Coordinates = {
  latitude: number;
  longitude: number;
};

type GeocodeStatus = "idle" | "loading" | "notFound" | "error";

type Point = {
  x: number;
  y: number;
};

type Tile = {
  key: string;
  left: number;
  top: number;
  url: string;
};

const DEFAULT_MAP_ZOOM = 16;
const MAP_DRAG_THRESHOLD = 4;
const MAX_MAP_ZOOM = 19;
const MIN_MAP_ZOOM = 3;
const PINCH_ZOOM_THRESHOLD = 1.18;
const TILE_SIZE = 256;

const parseCoordinates = (value: string): Coordinates | null => {
  const normalizedValue = value
    .trim()
    .replace(/\s*,\s*/g, ",")
    .replace(/\s+/g, ",");
  const [, latitudeValue, longitudeValue] = normalizedValue.match(/^(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/) || [];

  if (!latitudeValue || !longitudeValue) {
    return null;
  }

  const latitude = Number(latitudeValue);
  const longitude = Number(longitudeValue);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    return null;
  }

  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return null;
  }

  return { latitude, longitude };
};

const clampLatitude = (latitude: number) => Math.max(-85.05112878, Math.min(85.05112878, latitude));

const getScale = (zoom: number) => TILE_SIZE * 2 ** zoom;

const coordinatesToPoint = ({ latitude, longitude }: Coordinates, zoom: number): Point => {
  const scale = getScale(zoom);
  const safeLatitude = clampLatitude(latitude);
  const sinLatitude = Math.sin((safeLatitude * Math.PI) / 180);

  return {
    x: ((longitude + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)) * scale,
  };
};

const pointToCoordinates = ({ x, y }: Point, zoom: number): Coordinates => {
  const scale = getScale(zoom);
  const longitude = (x / scale) * 360 - 180;
  const latitudeRadians = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / scale)));

  return {
    latitude: (latitudeRadians * 180) / Math.PI,
    longitude,
  };
};

const getValidCoordinates = (latitude?: number, longitude?: number): Coordinates | null => {
  if (latitude === undefined || longitude === undefined) {
    return null;
  }

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    return null;
  }

  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return null;
  }

  return { latitude, longitude };
};

const LocationPage = () => {
  const { t } = useTranslation();

  const setDefaultValues = useCallback(
    (newly: boolean, section: LocationSection) => {
      if (newly) {
        section.pageTitle = t("pages.editor.location.header.title");
        section.address = "";
      }
      return section;
    },
    [t]
  );

  const { section, setSection, saveSection, hasUnsavedChanges } = useSection<LocationSection>(
    "location",
    setDefaultValues
  );

  const sectionRef = useRef(section);
  const addressEditedRef = useRef(false);
  const initialCoordinatesRef = useRef(getValidCoordinates(section.latitude, section.longitude));
  const mapCenterRef = useRef<Coordinates | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const activePointersRef = useRef(new Map<number, Point>());
  const pinchDistanceRef = useRef<number | null>(null);
  const panStateRef = useRef({
    hasDragged: false,
    isPointerDown: false,
    lastX: 0,
    lastY: 0,
  });
  const [geocodeStatus, setGeocodeStatus] = useState<GeocodeStatus>("idle");
  const [mapCenter, setMapCenter] = useState<Coordinates | null>(() =>
    getValidCoordinates(section.latitude, section.longitude)
  );
  const [mapSize, setMapSize] = useState({ width: 0, height: 0 });
  const [mapZoom, setMapZoom] = useState(DEFAULT_MAP_ZOOM);
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [isDraggingMap, setIsDraggingMap] = useState(false);

  sectionRef.current = section;
  mapCenterRef.current = mapCenter;

  const coordinates = useMemo(
    () => getValidCoordinates(section.latitude, section.longitude),
    [section.latitude, section.longitude]
  );

  const updateCoordinates = useCallback(
    (nextCoordinates: Coordinates, shouldCenterMap = false) => {
      if (shouldCenterMap) {
        setMapCenter(nextCoordinates);
      }

      setSection({
        ...sectionRef.current,
        latitude: nextCoordinates.latitude,
        longitude: nextCoordinates.longitude,
      });
    },
    [setSection]
  );

  useEffect(() => {
    const mapElement = mapRef.current;

    if (!mapElement) {
      return;
    }

    const updateMapSize = () => {
      const { width, height } = mapElement.getBoundingClientRect();
      setMapSize({ width, height });
    };

    updateMapSize();

    const resizeObserver = new ResizeObserver(updateMapSize);
    resizeObserver.observe(mapElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const address = section.address?.trim();
    const savedCoordinates = initialCoordinatesRef.current;

    if (!addressEditedRef.current && savedCoordinates) {
      setGeocodeStatus("idle");

      if (!mapCenterRef.current) {
        setMapCenter(savedCoordinates);
      }

      return;
    }

    if (!address) {
      setGeocodeStatus("idle");
      setSection({
        ...sectionRef.current,
        latitude: undefined,
        longitude: undefined,
      });
      setMapCenter(null);
      return;
    }

    const parsedCoordinates = parseCoordinates(address);

    if (parsedCoordinates) {
      setGeocodeStatus("idle");
      updateCoordinates(parsedCoordinates, true);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        setGeocodeStatus("loading");
        const searchParams = new URLSearchParams({
          format: "json",
          limit: "1",
          q: address,
        });

        const response = await fetch(`https://nominatim.openstreetmap.org/search?${searchParams.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Failed to geocode address");
        }

        const [result] = (await response.json()) as { lat: string; lon: string }[];

        if (!result) {
          setGeocodeStatus("notFound");
          setSection({
            ...sectionRef.current,
            latitude: undefined,
            longitude: undefined,
          });
          setMapCenter(null);
          return;
        }

        const nextCoordinates = {
          latitude: Number(result.lat),
          longitude: Number(result.lon),
        };

        setGeocodeStatus("idle");
        updateCoordinates(nextCoordinates, true);
      } catch (error) {
        if (!controller.signal.aborted) {
          setGeocodeStatus("error");
        }
      }
    }, 650);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [section.address, setSection, updateCoordinates]);

  const tiles = useMemo<Tile[]>(() => {
    if (!mapCenter || !mapSize.width || !mapSize.height) {
      return [];
    }

    const tileZoom = Math.round(mapZoom);
    const centerPoint = coordinatesToPoint(mapCenter, tileZoom);
    const centerTileX = Math.floor(centerPoint.x / TILE_SIZE);
    const centerTileY = Math.floor(centerPoint.y / TILE_SIZE);
    const horizontalRadius = Math.ceil(mapSize.width / TILE_SIZE / 2) + 1;
    const verticalRadius = Math.ceil(mapSize.height / TILE_SIZE / 2) + 1;
    const worldTileCount = 2 ** tileZoom;
    const nextTiles: Tile[] = [];

    for (let x = centerTileX - horizontalRadius; x <= centerTileX + horizontalRadius; x += 1) {
      for (let y = centerTileY - verticalRadius; y <= centerTileY + verticalRadius; y += 1) {
        if (y < 0 || y >= worldTileCount) {
          continue;
        }

        const wrappedX = ((x % worldTileCount) + worldTileCount) % worldTileCount;

        nextTiles.push({
          key: `${wrappedX}-${y}`,
          left: x * TILE_SIZE - centerPoint.x + mapSize.width / 2,
          top: y * TILE_SIZE - centerPoint.y + mapSize.height / 2,
          url: `https://tile.openstreetmap.org/${tileZoom}/${wrappedX}/${y}.png`,
        });
      }
    }

    return nextTiles;
  }, [mapCenter, mapSize.height, mapSize.width, mapZoom]);

  const markerPosition = useMemo(() => {
    if (!coordinates || !mapCenter || !mapSize.width || !mapSize.height) {
      return null;
    }

    const tileZoom = Math.round(mapZoom);
    const centerPoint = coordinatesToPoint(mapCenter, tileZoom);
    const selectedPoint = coordinatesToPoint(coordinates, tileZoom);

    return {
      left: selectedPoint.x - centerPoint.x + mapSize.width / 2,
      top: selectedPoint.y - centerPoint.y + mapSize.height / 2,
    };
  }, [coordinates, mapCenter, mapSize.height, mapSize.width, mapZoom]);

  const isSaveDisabled = !section.pageTitle || !section.address;

  const getMapPlaceholder = () => {
    if (geocodeStatus === "loading") {
      return t("pages.editor.location.form.map.loading");
    }

    if (geocodeStatus === "notFound") {
      return t("pages.editor.location.form.map.notFound");
    }

    if (geocodeStatus === "error") {
      return t("pages.editor.location.form.map.error");
    }

    return t("pages.editor.location.form.map.empty");
  };

  const getCoordinatesFromPointerEvent = (event: PointerEvent<HTMLDivElement | HTMLButtonElement>) => {
    if (!mapRef.current || !mapCenter) {
      return null;
    }

    const rect = mapRef.current.getBoundingClientRect();
    const centerPoint = coordinatesToPoint(mapCenter, Math.round(mapZoom));

    return pointToCoordinates(
      {
        x: centerPoint.x + event.clientX - rect.left - rect.width / 2,
        y: centerPoint.y + event.clientY - rect.top - rect.height / 2,
      },
      Math.round(mapZoom)
    );
  };

  const updateZoom = useCallback(
    (nextZoom: number, anchor: Point) => {
      const currentMapCenter = mapCenterRef.current;
      const mapElement = mapRef.current;

      if (!currentMapCenter || !mapElement) {
        return;
      }

      const clampedZoom = Math.max(MIN_MAP_ZOOM, Math.min(MAX_MAP_ZOOM, nextZoom));

      if (clampedZoom === mapZoom) {
        return;
      }

      const rect = mapElement.getBoundingClientRect();
      const currentZoom = Math.round(mapZoom);
      const newZoom = Math.round(clampedZoom);
      const anchorOffset = {
        x: anchor.x - rect.left - rect.width / 2,
        y: anchor.y - rect.top - rect.height / 2,
      };
      const currentCenterPoint = coordinatesToPoint(currentMapCenter, currentZoom);
      const anchoredCoordinates = pointToCoordinates(
        {
          x: currentCenterPoint.x + anchorOffset.x,
          y: currentCenterPoint.y + anchorOffset.y,
        },
        currentZoom
      );
      const nextAnchoredPoint = coordinatesToPoint(anchoredCoordinates, newZoom);
      const nextMapCenter = pointToCoordinates(
        {
          x: nextAnchoredPoint.x - anchorOffset.x,
          y: nextAnchoredPoint.y - anchorOffset.y,
        },
        newZoom
      );

      mapCenterRef.current = nextMapCenter;
      setMapCenter(nextMapCenter);
      setMapZoom(clampedZoom);
    },
    [mapZoom]
  );

  const getPinchInfo = () => {
    const pointers = Array.from(activePointersRef.current.values());

    if (pointers.length < 2) {
      return null;
    }

    const [firstPointer, secondPointer] = pointers;

    return {
      center: {
        x: (firstPointer.x + secondPointer.x) / 2,
        y: (firstPointer.y + secondPointer.y) / 2,
      },
      distance: Math.hypot(secondPointer.x - firstPointer.x, secondPointer.y - firstPointer.y),
    };
  };

  const handleMapPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!mapCenter) {
      return;
    }

    activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (activePointersRef.current.size >= 2) {
      pinchDistanceRef.current = getPinchInfo()?.distance || null;
      panStateRef.current.isPointerDown = false;
      setIsDraggingMap(false);
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    panStateRef.current = {
      hasDragged: false,
      isPointerDown: true,
      lastX: event.clientX,
      lastY: event.clientY,
    };
  };

  const handleMapPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (activePointersRef.current.has(event.pointerId)) {
      activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }

    const pinchInfo = getPinchInfo();

    if (pinchInfo) {
      const previousPinchDistance = pinchDistanceRef.current;

      if (!previousPinchDistance) {
        pinchDistanceRef.current = pinchInfo.distance;
        return;
      }

      if (pinchInfo.distance / previousPinchDistance > PINCH_ZOOM_THRESHOLD) {
        updateZoom(mapZoom + 1, pinchInfo.center);
        pinchDistanceRef.current = pinchInfo.distance;
      }

      if (previousPinchDistance / pinchInfo.distance > PINCH_ZOOM_THRESHOLD) {
        updateZoom(mapZoom - 1, pinchInfo.center);
        pinchDistanceRef.current = pinchInfo.distance;
      }

      return;
    }

    const panState = panStateRef.current;
    const currentMapCenter = mapCenterRef.current;

    if (!panState.isPointerDown || !currentMapCenter) {
      return;
    }

    const deltaX = event.clientX - panState.lastX;
    const deltaY = event.clientY - panState.lastY;

    if (
      !panState.hasDragged &&
      Math.hypot(event.clientX - panState.lastX, event.clientY - panState.lastY) < MAP_DRAG_THRESHOLD
    ) {
      return;
    }

    panStateRef.current = {
      hasDragged: true,
      isPointerDown: true,
      lastX: event.clientX,
      lastY: event.clientY,
    };
    setIsDraggingMap(true);

    const currentZoom = Math.round(mapZoom);
    const centerPoint = coordinatesToPoint(currentMapCenter, currentZoom);
    const nextMapCenter = pointToCoordinates(
      {
        x: centerPoint.x - deltaX,
        y: centerPoint.y - deltaY,
      },
      currentZoom
    );

    mapCenterRef.current = nextMapCenter;
    setMapCenter(nextMapCenter);
  };

  const handleMapPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const panState = panStateRef.current;

    activePointersRef.current.delete(event.pointerId);
    pinchDistanceRef.current = getPinchInfo()?.distance || null;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    panStateRef.current = {
      hasDragged: false,
      isPointerDown: false,
      lastX: 0,
      lastY: 0,
    };
    setIsDraggingMap(false);

    if (panState.hasDragged || activePointersRef.current.size > 0) {
      return;
    }

    const nextCoordinates = getCoordinatesFromPointerEvent(event);

    if (nextCoordinates) {
      updateCoordinates(nextCoordinates);
    }
  };

  const handleMapPointerCancel = () => {
    activePointersRef.current.clear();
    pinchDistanceRef.current = null;
    panStateRef.current = {
      hasDragged: false,
      isPointerDown: false,
      lastX: 0,
      lastY: 0,
    };
    setIsDraggingMap(false);
  };

  const handleMapWheel = (event: WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    updateZoom(mapZoom + (event.deltaY < 0 ? 1 : -1), {
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMarkerPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDraggingMarker(true);
  };

  const handleMarkerPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (!isDraggingMarker) {
      return;
    }

    const nextCoordinates = getCoordinatesFromPointerEvent(event);

    if (nextCoordinates) {
      updateCoordinates(nextCoordinates);
    }
  };

  const handleMarkerPointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsDraggingMarker(false);
  };

  const handleMarkerPointerCancel = () => {
    setIsDraggingMarker(false);
  };

  return (
    <Container>
      <SubHeader
        title={t("pages.editor.location.header.title")}
        crumbs={[{ label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" }]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <Form>
        <Box>
          <FormGroup>
            <Input
              id="pageTitle"
              type="text"
              value={section.pageTitle}
              onChange={e =>
                setSection({
                  ...section,
                  pageTitle: e.target.value,
                })
              }
              label={t("pages.editor.location.form.pageTitle")}
            />
          </FormGroup>
          <FormGroup>
            <Input
              id="address"
              type="text"
              value={section.address}
              onChange={e => {
                addressEditedRef.current = true;
                setSection({
                  ...section,
                  address: e.target.value,
                });
              }}
              label={t("pages.editor.location.form.address")}
            />
          </FormGroup>
          {/* <div className="map-label">{t("pages.editor.location.form.labelPreview")}</div> */}
          <MapPreview
            ref={mapRef}
            $isDragging={isDraggingMap}
            onPointerCancel={handleMapPointerCancel}
            onPointerDown={handleMapPointerDown}
            onPointerMove={handleMapPointerMove}
            onPointerUp={handleMapPointerUp}
            onWheel={handleMapWheel}>
            {mapCenter ? (
              <>
                <div className="map-tiles" aria-hidden="true">
                  {tiles.map(tile => (
                    <img
                      key={tile.key}
                      alt=""
                      draggable={false}
                      src={tile.url}
                      style={{ left: tile.left, top: tile.top }}
                    />
                  ))}
                </div>
                {markerPosition && (
                  <button
                    aria-label={t("pages.editor.location.form.map.marker")}
                    className="map-marker"
                    onPointerDown={handleMarkerPointerDown}
                    onPointerMove={handleMarkerPointerMove}
                    onPointerCancel={handleMarkerPointerCancel}
                    onPointerUp={handleMarkerPointerUp}
                    style={{ left: markerPosition.left, top: markerPosition.top }}
                    type="button"
                  />
                )}
                <span className="map-help">{t("pages.editor.location.form.map.help")}</span>
              </>
            ) : (
              <div className="map-placeholder">{getMapPlaceholder()}</div>
            )}
          </MapPreview>
        </Box>
        <SaveBar onSave={saveSection} disabled={isSaveDisabled} hasUnsavedChanges={hasUnsavedChanges} />
      </Form>
    </Container>
  );
};

export default LocationPage;
